import { buildTranslateString, type EmailTranslations } from '@/utils/email-translations';
import type { TranslateString } from '@/types/translation.types';
import { getDefaultBlocksForTemplate } from './default-blocks';
import { templateStaticDefaults, type TemplateStaticDefaults } from './template-defaults';
import { BLOCK_ID } from './widgets/block-ids';
import type { EmailBlockContentV2 } from './widgets/block-types';
import { WidgetType } from './widgets/widget-registry';

const TEXT_BLOCK_TYPES = new Set<WidgetType>([
  WidgetType.Header,
  WidgetType.Paragraph,
  WidgetType.Footer,
  WidgetType.Button,
]);

type TemplateTextField = keyof TemplateStaticDefaults;

const BLOCK_FIELD_CANDIDATES: Partial<Record<string, TemplateTextField[]>> = {
  [BLOCK_ID.salutation]: ['salutation'],
  [BLOCK_ID.title]: ['title'],
  [BLOCK_ID.description]: ['description', 'descriptionTemplate'],
  [BLOCK_ID.cta]: ['cta'],
  [BLOCK_ID.badge]: ['liveBadgeText', 'watchOnDemandBadgeText'],
  [BLOCK_ID.tagline]: ['taglineText'],
  [BLOCK_ID.supportText]: ['supportText'],
  [BLOCK_ID.communityText]: ['communityText'],
  [BLOCK_ID.legalText]: ['complianceText'],
  [BLOCK_ID.mailingAddress]: ['footerText'],
};

const EVENT_TITLE_FALLBACK_BLOCKS = new Set<string>([BLOCK_ID.title, BLOCK_ID.salutation]);

export function templateFieldKey(templateId: string, field: TemplateTextField): string {
  return `${templateId}.${field}`;
}

function i18nKeyForBlock(templateId: string, blockId: string): string | undefined {
  const defaults = templateStaticDefaults[templateId];
  const candidates = BLOCK_FIELD_CANDIDATES[blockId];
  if (!defaults || !candidates) return undefined;

  const field = candidates.find((candidate) => defaults[candidate] !== undefined);
  return field ? templateFieldKey(templateId, field) : undefined;
}

export interface TranslatedDefaultBlocksArgs {
  templateId: string;
  languages: string[];
  baseLanguage: string;
  translations: EmailTranslations;
  eventTitleFor: (language: string) => string;
}

export function buildTranslatedDefaultBlocks({
  templateId,
  languages,
  baseLanguage,
  translations,
  eventTitleFor,
}: TranslatedDefaultBlocksArgs): EmailBlockContentV2[] | undefined {
  const blocks = getDefaultBlocksForTemplate(templateId, eventTitleFor(baseLanguage));
  if (!blocks) return undefined;

  return blocks.map((block) => {
    // the english copy baked into the default block - a last resort, not the base
    const englishBase = (block.props as { value?: TranslateString }).value;
    if (!TEXT_BLOCK_TYPES.has(block.type) || block.locked || !englishBase) return block;

    const key = i18nKeyForBlock(templateId, block.id);

    // seed from the project's base language so `base` never carries english copy. blocks with
    // no static text of their own resolve to '' here and are filled from eventTitleFor below.
    const fallback = (key ? translations.get(baseLanguage)?.[key] : undefined) ?? englishBase.base ?? '';

    const value = buildTranslateString({
      key,
      fallback,
      languages,
      baseLanguage,
      translations,
      eventTitleFor: key || EVENT_TITLE_FALLBACK_BLOCKS.has(block.id) ? eventTitleFor : undefined,
    });

    return { ...block, props: { ...block.props, value } };
  });
}
